<script>
import CalendarResource from '@/resources/calendar'
import UserResource from '@/resources/user'
import LocationResource from '@/resources/location'
import ReservationSeatingGroupResource from '@/resources/reservationSeatingGroup'
import ReservationSeatedUserResource from '@/resources/reservationSeatedUser'
import { PartyResource } from '@/resources/launcher'
import messages from '@/mixins/messages'
import CalendarReservationsPlanner from '../components/CalendarReservationsPlanner.vue'
import LoadingReservations from '../components/LoadingReservations.vue'

export default {
    name: 'AdminCalendar',
    mixins: [messages],
    components: {
        CalendarReservationsPlanner,
        LoadingReservations,
    },
    computed: {
        filters() {
            const filters = {}

            if (this.seatSearchValue) {
                filters.search = this.seatSearchValue
            }

            if (this.selectedLocation) {
                filters.location = this.selectedLocation.id
            }

            return filters
        },
        feedLinkUrl() {
            if (!this.selectedLocation) {
                return `${this.$appConfig('API_BASE_URL')}/feed.ics`
            }

            return `${this.$appConfig('API_BASE_URL')}/locations/${this.selectedLocation?.id}/feed.ics`
        },
        displayedCalendars() {
            if (this.activeGroup && this.groupCalendars.length) {
                return this.groupCalendars
            }
            return this.calendarResources
        },
        groupTrayLabel() {
            return this.groupTray.map((entry) => entry.label).join(', ')
        },
    },
    data() {
        const now = new Date()
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        return {
            now,
            today,
            loading: true,
            zoomLevel: parseFloat(localStorage.getItem('adminPlannerZoom')) || 1,
            selectedDate: today,
            calendarResources: [],
            locationResources: [],
            selectedLocation: null,
            seatSearchValue: '',
            seatSearchValueEntered: '',
            userSearchValue: '',
            userSearchValueEntered: '',
            userSearchResources: [],
            userSearchSelectMode: false,
            userSearchForReservations: [],
            groupTray: [],
            creatingGroup: false,
            activeGroup: null,
            activeGroupId: null,
            groupCalendars: [],
            launchingParty: false,
            seatAssignReservation: null,
            seatUserQuery: '',
            seatUserResults: [],
            seatUserSearching: false,
        }
    },
    watch: {
        zoomLevel() {
            localStorage.setItem('adminPlannerZoom', this.zoomLevel)
        },
    },
    methods: {
        selectedDateChange() {
            // Do nothing
        },
        async load() {
            this.calendarResources = []
            this.locationResources = await LocationResource.all()

            if (this.$route.query.location && !this.selectedLocation) {
                this.selectedLocation = this.locationResources.find((location) => location.id == this.$route.query.location)
            } else if (!this.selectedLocation) {
                this.selectedLocation = this.locationResources[0]
            } else if (this.selectedLocation && !this.$route.query?.location !== this.selectedLocation.id) {
                this.$router.replace(Object.assign({}, this.$route, { query: Object.assign({}, this.$route.query, { location: this.selectedLocation.id }) }))
            }

            this.seatSearchValueEntered = this.seatSearchValue
            this.calendarResources = await CalendarResource.filterAll(this.filters)
        },
        async searchUsers() {
            this.$refs.modalUserSearch.show()
            this.userSearchValueEntered = this.userSearchValue
            const result = await UserResource.search(this.userSearchValue)
            this.userSearchResources = result.resources
        },
        onCalendarScroll(pixels, c) {
            this.$refs.calendarPlanners.forEach((component, i) => component.scrollY(pixels))
        },
        onClearSeatSearch() {
            this.seatSearchValue = ''
            this.load()
        },
        onSeatUser(reservations) {
            this.userSearchForReservations = reservations
            this.userSearchSelectMode = true
            this.$refs.modalUserSearch.show()
        },
        async onSelectUser(user) {
            if (this.userSearchForReservations.length > 0) {
                const savePromises = []

                for (let reserv of this.userSearchForReservations) {
                    reserv.attributes.user = user.id
                    reserv.attributes.paid = true
                    savePromises.push(reserv.save())
                }

                await Promise.all(savePromises)
                await this.load()
                this.userSearchForReservations = []
            }

            this.userSearchSelectMode = false
            this.$refs.modalUserSearch.hide()
        },
        onUserSearchModalClose() {
            this.userSearchForReservations = []
            this.userSearchSelectMode = false
        },
        assignSeatUser(seat) {
            this.seatAssignReservation = seat.reservation === this.seatAssignReservation ? null : seat.reservation
            this.seatUserQuery = ''
            this.seatUserResults = []
        },
        cancelSeatAssign() {
            this.seatAssignReservation = null
            this.seatUserQuery = ''
            this.seatUserResults = []
        },
        async runSeatUserSearch() {
            if (!this.seatUserQuery) {
                return
            }
            this.seatUserSearching = true
            try {
                const result = await UserResource.search(this.seatUserQuery)
                this.seatUserResults = result.resources
            } finally {
                this.seatUserSearching = false
            }
        },
        async pickSeatUser(user) {
            try {
                await ReservationSeatedUserResource.assign(this.seatAssignReservation, user.id)
                this.cancelSeatAssign()
                await this.refreshActiveGroup()
            } catch (e) {
                this.$bvToast.toast(this.getResponseMessageFromAxiosException(e) || 'Could not assign this racer.', { variant: 'danger', title: 'Assignment failed' })
            }
        },
        async clearSeatUser(seat) {
            if (!seat.seated_assignment) {
                return
            }
            try {
                await ReservationSeatedUserResource.clear(seat.seated_assignment)
                await this.refreshActiveGroup()
            } catch (e) {
                this.$bvToast.toast(this.getResponseMessageFromAxiosException(e) || 'Could not clear this seat.', { variant: 'danger', title: 'Clear failed' })
            }
        },
        async refreshActiveGroup() {
            if (!this.activeGroupId) {
                return
            }
            this.activeGroup = await ReservationSeatingGroupResource.detail(this.activeGroupId)
            if (this.$refs.calendarPlanners) {
                this.$refs.calendarPlanners.forEach((planner) => planner.refresh())
            }
        },
        onAddToGroup(reservations) {
            for (let reserv of reservations) {
                if (!this.groupTray.find((entry) => entry.id === reserv.id)) {
                    this.groupTray.push({ id: reserv.id, label: `${reserv.get('calendar_name') || 'Sim'} @ ${reserv.get('time') ? new Date(reserv.get('time')).toLocaleString() : reserv.get('slot')}` })
                }
            }
        },
        clearGroupTray() {
            this.groupTray = []
        },
        async createGroupFromTray() {
            if (this.groupTray.length < 2) {
                return
            }
            this.creatingGroup = true
            try {
                const group = await ReservationSeatingGroupResource.createGroup({
                    source: 'MANUAL',
                    reservations: this.groupTray.map((entry) => entry.id),
                })
                this.groupTray = []
                await this.filterToGroup(group.id)
                this.$bvToast.toast('Simulators grouped. Use "Group details" to see the roster.', { variant: 'success', title: 'Group created' })
            } catch (e) {
                this.$bvToast.toast(this.getResponseMessageFromAxiosException(e) || 'Could not group these simulators.', { variant: 'danger', title: 'Grouping failed' })
            }
            this.creatingGroup = false
        },
        syncGroupQuery(groupId) {
            const query = Object.assign({}, this.$route.query)
            if (groupId) {
                query.group = groupId
            } else {
                delete query.group
            }
            this.$router.replace(Object.assign({}, this.$route, { query })).catch(() => {})
        },
        async filterToGroup(groupId) {
            try {
                this.activeGroupId = groupId
                this.activeGroup = await ReservationSeatingGroupResource.detail(groupId)
                const ids = this.activeGroup.calendarIds()
                let pool = this.calendarResources.filter((calendar) => ids.includes(calendar.id))
                if (pool.length < ids.length && this.selectedLocation) {
                    const locationCalendars = await CalendarResource.filterAll({ location: this.selectedLocation.id })
                    pool = locationCalendars.filter((calendar) => ids.includes(calendar.id))
                }
                this.groupCalendars = ids.map((id) => pool.find((calendar) => calendar.id === id)).filter(Boolean)
                this.syncGroupQuery(groupId)
            } catch (e) {
                this.activeGroupId = null
                this.activeGroup = null
                this.groupCalendars = []
                this.$bvToast.toast(this.getResponseMessageFromAxiosException(e) || 'Could not open that group.', { variant: 'danger', title: 'Group unavailable' })
            }
        },
        openGroupDetails() {
            if (this.activeGroup) {
                this.$refs.groupPanel.show()
            }
        },
        clearGroupFilter() {
            this.activeGroup = null
            this.activeGroupId = null
            this.groupCalendars = []
            this.cancelSeatAssign()
            this.syncGroupQuery(null)
        },
        async launchParty() {
            if (!this.activeGroup) {
                return
            }
            this.launchingParty = true
            try {
                const calendars = this.groupCalendars
                const missingEvents = calendars.filter((calendar) => !calendar.get('event'))
                if (calendars.length < 2) {
                    this.$bvToast.toast('This group needs at least two simulators with loaded sessions.', { variant: 'warning', title: 'Cannot create party' })
                    return
                }
                if (missingEvents.length) {
                    this.$bvToast.toast(`Start these simulators on the launcher first: ${missingEvents.map(String).join(', ')}`, { variant: 'warning', title: 'Sessions not ready' })
                    return
                }
                const [host, ...others] = calendars
                const party = await PartyResource.host(
                    host.id,
                    `${this.activeGroup.bookedByName()}'s Party`,
                    others.map((calendar) => calendar.id)
                )
                if (party) {
                    this.$bvToast.toast('Party created and entries added. Reload the launchers to go green.', { variant: 'success', title: 'Party ready' })
                } else {
                    this.$bvToast.toast('The party could not be created.', { variant: 'danger', title: 'Create Party failed' })
                }
            } catch (e) {
                this.$bvToast.toast(this.getResponseMessageFromAxiosException(e) || 'Could not create the party.', { variant: 'danger', title: 'Create Party failed' })
            } finally {
                this.launchingParty = false
            }
        },
    },
    async mounted() {
        await this.load()
        this.loading = false
        if (this.$route.query.group) {
            await this.filterToGroup(this.$route.query.group)
        }
    },
}
</script>

<template>
    <div>
        <b-container class="mt-3" fluid>
            <b-row>
                <b-col md="3">
                    <b-card bg-variant="light" class="mb-3">
                        <b-calendar v-model="selectedDate" block :hide-header="true" value-as-date @input="selectedDateChange" ref="calendar" selected-variant="success" today-variant="info" locale="en"></b-calendar>
                    </b-card>

                    <b-card>
                        <b-form @submit.prevent="searchUsers()">
                            <b-form-group label="Benutzer suchen">
                                <b-input-group>
                                    <b-input v-model="userSearchValue" />
                                    <b-input-group-append>
                                        <b-button type="submit" variant="primary">Los</b-button>
                                    </b-input-group-append>
                                </b-input-group>
                            </b-form-group>
                        </b-form>
                        <b-form @submit.prevent="load()">
                            <b-form-group>
                                <template #label>
                                    Plätze filtern
                                    <b-button v-if="seatSearchValueEntered" size="sm" variant="link" class="p-0 ml-1 align-baseline" @click="onClearSeatSearch()"> „{{ seatSearchValueEntered }}“ löschen <b-icon icon="x-circle" /> </b-button>
                                </template>
                                <b-input-group label="Nach Platz filtern">
                                    <b-form-input v-model="seatSearchValue" />
                                    <b-input-group-append>
                                        <b-button v-if="seatSearchValueEntered" variant="outline-secondary" title="Filter löschen" @click="onClearSeatSearch()"><b-icon icon="x" /></b-button>
                                        <b-button type="submit" variant="primary" @click="load()">Los</b-button>
                                    </b-input-group-append>
                                </b-input-group>
                            </b-form-group>
                        </b-form>

                        <b-form @submit.prevent="load()" v-if="locationResources.length > 1">
                            <b-form-group>
                                <template #label> Standort filtern </template>
                                <b-input-group label="Nach Standort filtern">
                                    <b-form-select v-model="selectedLocation">
                                        <b-form-select-option v-for="(loc, i) in locationResources" :key="i" :value="loc">{{ loc }}</b-form-select-option>
                                    </b-form-select>
                                    <b-input-group-append>
                                        <b-button type="submit" variant="primary" @click="load()">Los</b-button>
                                    </b-input-group-append>
                                </b-input-group>
                            </b-form-group>
                        </b-form>

                        <b-form-group label="URL des iCalendar-Feeds">
                            <b-form-input :value="feedLinkUrl" readonly />
                        </b-form-group>
                        <b-form-group label="Zoom">
                            <b-spinbutton v-model="zoomLevel" step="0.1" min="0.1" />
                        </b-form-group>
                    </b-card>
                </b-col>
                <b-col v-if="loading">
                    <loading-reservations />
                </b-col>

                <b-col v-else class="calendars">
                    <b-alert :show="!!activeGroup" variant="info" class="group-filter-alert d-flex align-items-center">
                        <span class="mr-2"
                            >Gefiltert nach der Gruppe von <strong>{{ activeGroup && activeGroup.bookedByName() }}</strong
                            ></span
                        >
                        <b-button size="sm" variant="outline-primary" class="mr-2" @click="openGroupDetails()">Gruppendetails</b-button>
                        <b-button size="sm" variant="outline-secondary" @click="clearGroupFilter()"><b-icon icon="x" /> Filter löschen</b-button>
                    </b-alert>

                    <div :style="`width: ${displayedCalendars.length * 520}px; zoom: ${zoomLevel}`">
                        <b-card class="calendar ml-3" v-for="(calendar, i) in displayedCalendars" :key="calendar.id || i">
                            <calendar-reservations-planner title="Kalender" :calendar-id="calendar.id" :for-date="selectedDate" ref="calendarPlanners" @scroll="(scrollY) => onCalendarScroll(scrollY, calendar)" @seatUser="onSeatUser" @addToGroup="onAddToGroup" @viewSeatingGroup="filterToGroup">
                            </calendar-reservations-planner>
                        </b-card>
                    </div>

                    <div v-if="!this.calendarResources.length && seatSearchValueEntered">
                        <b-card> Keine Plätze gefunden, die „{{ seatSearchValueEntered }}“ entsprechen </b-card>
                    </div>
                </b-col>
            </b-row>
        </b-container>

        <!-- Modals -->
        <b-modal title="Nach Benutzern suchen" ref="modalUserSearch" @hide="onUserSearchModalClose" no-fade centered hide-footer>
            <b-form @submit.prevent="searchUsers()">
                <b-form-group label="Suchanfrage">
                    <b-input-group>
                        <b-input v-model="userSearchValue" />
                        <b-input-group-append>
                            <b-button type="submit" variant="primary">Suchen</b-button>
                        </b-input-group-append>
                    </b-input-group>
                </b-form-group>
            </b-form>

            <div v-if="userSearchResources.length">
                <hr />
                <p>Suchergebnisse für „{{ userSearchValueEntered }}“</p>
                <b-list-group>
                    <b-list-group-item v-for="(user, i) in userSearchResources" :key="i">
                        <a v-if="userSearchSelectMode" v-b-tooltip.hover title="Diesen Benutzer auswählen" href="javascript:{}" @click="onSelectUser(user)"
                            >{{ user }} <span class="text-muted">- {{ user.attributes.email }}</span></a
                        >
                        <router-link v-else :to="{ name: 'adminUser', params: { userId: user.id } }">
                            {{ user }} <span class="text-muted" v-if="user.attributes.is_onboarded">- {{ user.attributes.email }}</span>
                            <b-badge pill variant="success" class="float-right ml-2" v-if="user.attributes.is_staff">Mitarbeiter</b-badge>
                            <b-badge pill variant="success" class="float-right ml-2" v-if="user.attributes.credits_subscription_exists">Abonnent</b-badge>
                            <b-badge pill variant="danger" class="float-right ml-2" v-if="user.attributes.credits_subscription_exists && !user.attributes.credits_subscription_in_good_standing">Zahlungsrückstand</b-badge>
                        </router-link>
                    </b-list-group-item>
                </b-list-group>
            </div>

            <p v-else-if="userSearchValueEntered && !userSearchResources.length">Keine Ergebnisse für „{{ userSearchValueEntered }}“</p>
        </b-modal>

        <b-modal ref="groupPanel" title="Platzgruppe" size="lg" ok-only ok-title="Schließen" no-fade scrollable @hide="cancelSeatAssign()">
            <template v-if="activeGroup">
                <dl class="row">
                    <dt class="col-sm-4">Gebucht von</dt>
                    <dd class="col-sm-8">{{ activeGroup.bookedByName() }}</dd>
                    <dt class="col-sm-4">Gebucht am</dt>
                    <dd class="col-sm-8">{{ activeGroup.attributes.created ? new Date(activeGroup.attributes.created).toLocaleString() : 'Unbekannt' }}</dd>
                    <dt class="col-sm-4">Quelle</dt>
                    <dd class="col-sm-8">{{ activeGroup.attributes.source === 'MANUAL' ? 'Von Mitarbeitern gruppiert' : 'Aus dem Bezahlvorgang' }}</dd>
                </dl>

                <h6 class="mt-3">Simulatoren</h6>
                <b-list-group class="mb-3">
                    <b-list-group-item v-for="seat in activeGroup.seats()" :key="seat.reservation">
                        <div class="d-flex justify-content-between align-items-center">
                            <span
                                ><strong>{{ seat.calendar_name }}</strong> <span class="text-muted small ml-2">{{ seat.time ? new Date(seat.time).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : seat.slot }}</span></span
                            >
                            <span class="text-right">
                                <span class="mr-2">{{ seat.seated_user_display_name || 'Frei' }}</span>
                                <b-button size="sm" variant="outline-secondary" @click="assignSeatUser(seat)">{{ seatAssignReservation === seat.reservation ? 'Abbrechen' : seat.seated_user ? 'Ändern' : 'Zuweisen' }}</b-button>
                                <b-button v-if="seat.seated_assignment" size="sm" variant="link" class="text-muted" @click="clearSeatUser(seat)">löschen</b-button>
                            </span>
                        </div>

                        <div v-if="seatAssignReservation === seat.reservation" class="mt-2">
                            <b-input-group size="sm">
                                <b-form-input v-model="seatUserQuery" placeholder="Benutzer nach Name oder E-Mail-Adresse suchen" @keyup.enter="runSeatUserSearch()" />
                                <b-input-group-append>
                                    <b-button variant="primary" :disabled="seatUserSearching" @click="runSeatUserSearch()">Suchen</b-button>
                                </b-input-group-append>
                            </b-input-group>
                            <b-list-group class="mt-2" v-if="seatUserResults.length">
                                <b-list-group-item v-for="u in seatUserResults" :key="u.id" button class="py-1" @click="pickSeatUser(u)">
                                    {{ u }} <span class="text-muted small">{{ u.attributes.email }}</span>
                                </b-list-group-item>
                            </b-list-group>
                            <p v-else-if="seatUserQuery && !seatUserSearching" class="text-muted small mt-2 mb-0">Keine passenden Benutzer. Versuche eine andere Suche.</p>
                        </div>
                    </b-list-group-item>
                </b-list-group>

                <h6>Fahrer</h6>
                <b-list-group>
                    <b-list-group-item v-for="invitee in activeGroup.invitees()" :key="invitee.id" class="d-flex justify-content-between align-items-center">
                        <span>
                            <a :href="`${$appConfig('API_BASE_URL')}/admin/user/user/${invitee.user}`" target="_blank" class="mr-2">{{ invitee.user_display_name }}</a>
                            <span class="text-muted small">{{ invitee.email }}</span>
                        </span>
                        <span>
                            <b-badge v-if="invitee.is_purchaser" variant="primary" class="mr-1">Bucher</b-badge>
                            <b-badge v-if="invitee.assigned_reservation" variant="success">Zugewiesen</b-badge>
                        </span>
                    </b-list-group-item>
                </b-list-group>
            </template>

            <template #modal-footer="{ ok }">
                <b-button variant="secondary" :disabled="launchingParty" @click="launchParty()"> <b-spinner v-if="launchingParty" small /> Party erstellen &amp; starten </b-button>
                <b-button variant="primary" @click="ok()">Schließen</b-button>
            </template>
        </b-modal>

        <div v-if="groupTray.length" class="group-tray shadow">
            <span class="mr-3"
                ><strong>{{ groupTray.length }}</strong> Plätze zum Gruppieren ausgewählt</span
            >
            <span class="text-muted small mr-3">{{ groupTrayLabel }}</span>
            <b-button size="sm" variant="secondary" :disabled="groupTray.length < 2 || creatingGroup" @click="createGroupFromTray()"> <b-spinner v-if="creatingGroup" small /> Gruppe erstellen </b-button>
            <b-button size="sm" variant="link" @click="clearGroupTray()">Löschen</b-button>
        </div>
    </div>
</template>

<style lang="scss">
.calendars {
    overflow-x: scroll;

    .calendar {
        float: left;
        width: 500px;
    }
}

.group-filter-alert {
    position: sticky;
    left: 0;
    z-index: 5;
    display: inline-block;
}

.group-tray {
    position: fixed;
    bottom: 1rem;
    left: 50%;
    transform: translateX(-50%);
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 0.5rem;
    padding: 0.5rem 1rem;
    z-index: 1050;
    display: flex;
    align-items: center;
}
</style>
